import { useFrontendTool } from "@copilotkit/react-core";
import { extractMaskedEmail, isEmailMatch } from "@helpers/string-utils";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
import { events, useRedeemCopilotAnalytics } from "../analytics";
import type { AccessContentProps } from "../components/AccessContent";
import { COPILOT_ACTIONS } from "../constants/actions";
import { useRedeemCopilot } from "../context";
import { getCopilotAction } from "../helpers";
import { COPILOT_CHOICES } from "../instructions";
import type { ActionExecutionResult } from "../renderers";

export const useRcRedeemCode = ({
	handler: redeemCodeHandler,
}: {
	handler: ({ code }: { code: string }) => Promise<{
		success: boolean;
		error: string;
		isSameEmail?: boolean;
		bundleId?: string;
		result?: ApplyAccessCodeResponse;
		shouldHandleNext?: boolean;
	}>;
}) => {
	const {
		chatState: { userEmail },
	} = useRedeemCopilot();

	const { trackRedeemCopilotEvent } = useRedeemCopilotAnalytics();

	useFrontendTool({
		...getCopilotAction(COPILOT_ACTIONS.REDEEM_ACCESS_CODE),
		parameters: [
			{
				name: "accessCode",
				type: "string",
				description: "The access code to verify",
			},
		],

		handler: async ({
			accessCode,
		}): Promise<ActionExecutionResult<AccessContentProps>> => {
			const { success, error, result, isSameEmail } = await redeemCodeHandler({
				code: accessCode,
			});

			if (!success) {
				const type = "error";
				const { maskedEmail } = extractMaskedEmail(error);
				let matchesMaskedEmail = false;
				if (maskedEmail) {
					matchesMaskedEmail = isEmailMatch(maskedEmail, userEmail);
				}

				if (isSameEmail && matchesMaskedEmail) {
					const message =
						"The access code is already redeemed by the current user";

					trackRedeemCopilotEvent(events.redeemCodeFailed, {
						type,
						message,
						accessCode,
					});

					return {
						type,
						message,
						result: {
							data: result?.data,
							productData: result?.productData,
							success: false,
						},
						instructions: COPILOT_CHOICES.ACCESS_CODE_ALREADY_REDEEMED,
					};
				}

				trackRedeemCopilotEvent(events.redeemCodeFailed, {
					type,
					message: error,
					accessCode,
				});

				return {
					type,
					message: error,
					instructions: COPILOT_CHOICES.REDEEM_ACCESS_CODE_ERROR,
				};
			}

			const type = "success";
			const message = "Successfully redeemed the access code";

			trackRedeemCopilotEvent(events.redeemCodeSucceeded, {
				type,
				message,
				accessCode,
			});

			return {
				type,
				message,
				result: {
					data: result?.data,
					productData: result?.productData,
					success: true,
				},
				instructions: COPILOT_CHOICES.REDEEM_ACCESS_CODE_SUCCESS,
			};
		},
	});
};
